import type { LiveChangedBy } from "@nexxt/contracts/realtime";
import { partialMatchKey, type Query, type QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  use,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  browserConnect,
  createLiveClient,
  LIVE_RESERVE_POLL_MS,
  type LiveClient,
  type LivePresence,
  NOBODY_HERE,
  type RowsRead,
  rowsToLight,
} from "./live-client";

// Nexxt Live in the screens (ADR-278): a screen names its room and the queries that show it, and reads them again when
// the room signals a change. While the socket of a room is lost its screens read again every minute, and a query that
// polled before Nexxt Live polls as it did (`pollUnlessLive`), with the socket open at most every minute (ADR-285); a
// room that never had a grant (`LIVE` off) is the app as it was before.

let appClient: LiveClient | null = null;

/** The live client of the app, one for all its rooms: created on first use. */
const liveClient = (): LiveClient => (appClient ??= createLiveClient(browserConnect));

/**
 * What the api client of the app hands to it: the grants of the reads and the ids of the writes. Spread into the
 * options of the client (`backstageClient({ ...liveCapture, onUnauthenticated })`).
 */
export const liveCapture = {
  onLiveGrant: (grant: string) => liveClient().captureGrant(grant),
  onWrite: (requestId: string) => liveClient().rememberWrite(requestId),
};

const LiveContext = createContext<LiveClient | null>(null);

/**
 * Once around the app's router (`main.tsx`): the screens under it listen to their rooms; a page that names no room,
 * the sign-in pages too, opens nothing. Without it the screens are as before. Leaving it closes every socket.
 */
export function LiveProvider({ client = liveClient(), children }: { client?: LiveClient; children: ReactNode }) {
  useEffect(() => () => client.stop(), [client]);
  return <LiveContext value={client}>{children}</LiveContext>;
}

/**
 * The grant a room's last read gave, for another socket that lives by it (the assistant of the room, ADR-284): read
 * when the socket opens, so always the latest. Undefined without Nexxt Live or before the read.
 */
export function useRoomGrant(): (room: string) => string | undefined {
  const client = use(LiveContext);
  return (room) => client?.grantFor(room);
}

/**
 * After a read a signal asked for, the replica may not have had the change yet (Q-SEC-33, point 4): if nothing the
 * room's queries show came back different, they are read once more after this.
 */
const REPLICA_RETRY_MS = 1_500;

const ofKeys = (keys: readonly QueryKey[]) => (query: Query) =>
  keys.some((key) => partialMatchKey(query.queryKey, key));

/** The queries under `keys` read again; after a signal, once more later if every one came back as it was. */
export async function readAgain(
  queryClient: QueryClient,
  keys: readonly QueryKey[],
  signalled: boolean,
): Promise<void> {
  const predicate = ofKeys(keys);
  const before = new Map(
    queryClient
      .getQueryCache()
      .findAll({ predicate })
      .map((q) => [q.queryHash, q.state.data]),
  );
  await queryClient.invalidateQueries({ predicate });
  if (!signalled) return;
  const after = queryClient.getQueryCache().findAll({ predicate, type: "active" });
  // structural sharing keeps the same object for data that did not change
  if (after.length > 0 && after.every((q) => before.get(q.queryHash) === q.state.data)) {
    setTimeout(() => void readAgain(queryClient, keys, false), REPLICA_RETRY_MS);
  }
}

const noStatus = () => () => {};

/**
 * `read` at most once every `ms`: a call within `ms` of the last read waits for the end of it, and the calls of the
 * wait are one read, signalled if any of them was. Returns the call and the cancel of a read still waiting.
 */
export function atMostEvery(
  ms: number,
  read: (signalled: boolean) => void,
): [call: (signalled: boolean) => void, cancel: () => void] {
  let last = Number.NEGATIVE_INFINITY;
  let signalledSince = false;
  let waiting: ReturnType<typeof setTimeout> | undefined;
  const run = () => {
    waiting = undefined;
    last = Date.now();
    const signalled = signalledSince;
    signalledSince = false;
    read(signalled);
  };
  const call = (signalled: boolean) => {
    signalledSince ||= signalled;
    if (waiting) return;
    const wait = last + ms - Date.now();
    if (wait <= 0) run();
    else waiting = setTimeout(run, wait);
  };
  return [call, () => clearTimeout(waiting)];
}

/** Whether the socket of `room` is open, drawn again when it opens or closes. */
function useLiveStatus(room: string | undefined): boolean {
  const client = use(LiveContext);
  return useSyncExternalStore(
    client ? client.onStatus : noStatus,
    () => (client && room ? client.isLive(room) : false),
    () => false,
  );
}

/**
 * The screen listens to `room` (`entityRoom` of `@nexxt/contracts/realtime`, the aggregate it shows) and reads again
 * the queries under `keys` when it changes, or may have changed while the socket was not there. The room opens with
 * the grant of the read of the aggregate, so the screen calls it once that read answered. Returns whether the socket
 * is open: the component that polls passes it to `pollUnlessLive`, and draws again when it changes. `read`: what the
 * screen does instead, for a room that many writes signal (the counts of the home, read at most every 15 s, ADR-285).
 */
export function useLiveRoom(
  room: string | undefined,
  keys: readonly QueryKey[],
  { read }: { read?: () => void } = {},
): boolean {
  const client = use(LiveContext);
  const queryClient = useQueryClient();
  const changed = useEffectEvent((signalled: boolean) =>
    read ? read() : void readAgain(queryClient, keys, signalled),
  );
  useEffect(() => {
    if (!client || !room) return;
    return client.listen(room, { changed: (signalled) => changed(signalled) });
  }, [client, room]);
  return useLiveStatus(room);
}

/** Who else is in a room, and what the screen tells it of this user (ADR-279). */
export interface Presence extends LivePresence {
  /** The user is on `row` of the screen's grid (null: on none). */
  focus: (row: string | null) => void;
  /** The user is typing: the room hears it at most every two seconds. */
  typing: () => void;
}

/**
 * The screen shows who is here in `room` (ADR-279): while it is mounted the room knows where the user is, and tells
 * who else is there, each as this user may see them (internal users by name; an external user sees "Tomorrow" for
 * Tomorrow's people, B-131). `undefined`: the screen shows nobody now (a PO no longer in proposal). Nobody either
 * while the socket is not open. A component that said a row leaves it when it goes (a grid in a tab of the page).
 */
export function usePresence(room: string | undefined): Presence {
  const client = use(LiveContext);
  const toldRow = useRef(false);
  useEffect(() => {
    if (!client || !room) return;
    const leave = client.join(room);
    return () => {
      if (toldRow.current) client.focus(room, null);
      leave();
    };
  }, [client, room]);
  const presence = useSyncExternalStore(
    client ? client.onPresence : noStatus,
    () => (client && room ? client.presenceOf(room) : NOBODY_HERE),
    () => NOBODY_HERE,
  );
  return {
    ...presence,
    focus: (row) => {
      if (!client || !room) return;
      toldRow.current = true;
      client.focus(room, row);
    },
    typing: () => {
      if (client && room) client.typing(room);
    },
  };
}

/** How long the rows someone else changed stay highlighted. */
const HIGHLIGHT_MS = 4_000;

/** The rows lit by one change, by row id, with who made it, and the change's number (one announcement each). */
export interface LitRows {
  byRow: ReadonlyMap<string, LiveChangedBy>;
  seq: number;
}

const NO_CHANGES: LitRows = { byRow: new Map(), seq: 0 };

/**
 * The rows of a screen's grid someone else just changed, and who, as the room lets this user see them (ADR-308): the
 * first read that ends after the room names who made a change (`readAt`, the `dataUpdatedAt` of the screen's query)
 * spends it, and its rows that differ from the read before light for a few seconds (`rowsToLight`). A change of this
 * tab, a change the room names nobody for (a job), `undefined` or a room with no socket light nothing.
 */
export function useRowChanges<T>(
  room: string | undefined,
  rows: readonly T[],
  getRowId: (row: T) => string,
  readAt: number,
): LitRows {
  const client = use(LiveContext);
  const change = useSyncExternalStore(
    client ? client.onChange : noStatus,
    () => (client && room ? client.changeOf(room) : undefined),
    () => undefined,
  );
  const before = useRef<RowsRead<T> | null>(null);
  const spent = useRef(0);
  const [lit, setLit] = useState(NO_CHANGES);
  const compare = useEffectEvent(() => {
    const previous = before.current;
    const after = { rows, readAt };
    before.current = after;
    const { spend, lit: ids } = rowsToLight(previous, after, change, spent.current, getRowId, Date.now());
    if (!spend || !change) return;
    spent.current = change.seq;
    if (ids.length > 0) setLit({ byRow: new Map(ids.map((id) => [id, change.by])), seq: change.seq });
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: compared again when the rows, their read or the change move
  useEffect(() => compare(), [rows, readAt, change]);
  useEffect(() => {
    if (lit.byRow.size === 0) return;
    const off = setTimeout(() => setLit(NO_CHANGES), HIGHLIGHT_MS);
    return () => clearTimeout(off);
  }, [lit]);
  return lit;
}

/**
 * The `refetchInterval` of a query a tab shows, in a page that listens to `room` and reads the tab again
 * (`useLiveRoom` with the tab's key among its own): `pollUnlessLive` of whether that socket is open. The tab opens no
 * socket itself, so without a page that listens it polls as before (ADR-285).
 */
export function useLivePolling<Q>(
  room: string,
  interval: number | false | ((query: Q) => number | false | undefined),
): (query: Q) => number | false | undefined {
  return pollUnlessLive(useLiveStatus(room), interval);
}

/**
 * The room of a screen that follows the newest item of a list it polls (the latest run, the latest import, ADR-285):
 * `useLiveRoom` of the room the last answer named, under `keys`. The query polls with `pollUnlessLive` of this room,
 * so the room cannot come from the same render: it is kept from the render before, and `follow`, called in render
 * with the room of what the screen shows now, moves it. Returns whether the socket is open, and `follow`.
 */
export function useFollowedRoom(
  keys: readonly QueryKey[],
): [live: boolean, follow: (room: string | undefined) => void] {
  const [room, setRoom] = useState<string>();
  const live = useLiveRoom(room, keys);
  // the state of the render before, moved in render (react.dev, "Storing information from previous renders")
  const follow = (next: string | undefined) => {
    if (next !== room) setRoom(next);
  };
  return [live, follow];
}

/**
 * The `refetchInterval` of a query whose room the component listens to (`useLiveRoom`): `interval`, the one the query
 * had before Nexxt Live (a number, or a function of the query), while the socket is not open. With the socket open
 * the signal reads it again, and while the query still waits for something (an outcome, a run going on) it reads no
 * more often than once a minute: a signal is best effort, and what follows the clock (a run stalled, an outcome
 * overdue) has none (ADR-285).
 */
export function pollUnlessLive<Q>(
  live: boolean,
  interval: number | false | ((query: Q) => number | false | undefined),
): (query: Q) => number | false | undefined {
  return (query) => {
    const every = typeof interval === "function" ? interval(query) : interval;
    return live && every ? Math.max(every, LIVE_RESERVE_POLL_MS) : every;
  };
}
